/**
 * Payroll run — the run grid (Payroll Increment 2).
 *
 * Mirrors `prototype/payroll/payroll-run.html`: a 5-up iconed tile strip, the
 * "Flint records, doesn't calculate" honesty seam, and a per-employee grid
 * with editable TYPED deduction inputs (TDS/PF/ESI/PT/Other) that recompute
 * net live and persist on blur via `payrollRuns.updateLine`. A totals row, a
 * "Payslip" link per row (opens the drawer), and a docked footer with Save
 * draft / Submit for approval — and once approved, "Pay net to bank".
 *
 * The run is append-only: deduction inputs are editable only while `draft`;
 * once submitted/approved/paid the grid is read-only and a correction is a
 * new run.
 *
 * @see prototype/payroll/payroll-run.html
 * @see docs/product/payroll-v1-build-plan.md §"Inc 2 — Payroll run"
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  IndianRupee,
  Landmark,
  MinusCircle,
  UsersRound,
  Wallet,
} from "lucide-react";
import {
  Alert,
  DialogRoot,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@flint/ui";
import { paiseToInr, rupeesToPaiseString, paiseToRupeesInput } from "../../../lib/format.ts";
import { trpc } from "../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../features/workspace/index.ts";
import {
  PayrollKpiTiles,
  PayslipDrawer,
  periodLabel,
  statusPill,
  type RunLineView,
} from "../../../features/payroll/index.ts";

export const Route = createFileRoute("/(workspace)/payroll/$runId/")({
  component: PayrollRunDetailPage,
});

/** The five typed deduction columns. */
const DEDUCTIONS = [
  { key: "tdsPaise", label: "TDS" },
  { key: "pfPaise", label: "PF" },
  { key: "esiPaise", label: "ESI" },
  { key: "ptPaise", label: "PT" },
  { key: "otherDeductionPaise", label: "Other" },
] as const;
type DeductionKey = (typeof DEDUCTIONS)[number]["key"];

function PayrollRunDetailPage() {
  const { runId } = Route.useParams();
  const utils = trpc.useUtils();

  const runQ = trpc.payrollRuns.get.useQuery({ id: runId });
  const companyQ = trpc.companies.get.useQuery({});
  const accountsQ = trpc.bankAccounts.list.useQuery({});

  const [slipLine, setSlipLine] = useState<RunLineView | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payAccountId, setPayAccountId] = useState<string>("");

  const run = runQ.data;
  const isDraft = run?.status === "draft";
  const isApproved = run?.status === "approved";

  const updateLine = trpc.payrollRuns.updateLine.useMutation({
    onSuccess: () => {
      void utils.payrollRuns.get.invalidate({ id: runId });
    },
    onError: (err) => toast.error(err.message),
  });

  const submit = trpc.payrollRuns.submit.useMutation({
    onSuccess: async (res) => {
      await utils.payrollRuns.get.invalidate({ id: runId });
      await utils.payrollRuns.list.invalidate();
      if (res.outcome === "awaiting_approval") {
        toast.info("Sent for founder approval — it'll appear in the Inbox.");
      } else {
        toast.success("Run approved — ready to pay.");
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const pay = trpc.payrollRuns.pay.useMutation({
    onSuccess: async (res) => {
      setPayOpen(false);
      await Promise.all([
        utils.payrollRuns.get.invalidate({ id: runId }),
        utils.payrollRuns.list.invalidate(),
        utils.bankAccounts.list.invalidate(),
      ]);
      toast.success(`Paid · ${res.movementsPosted} salary entries posted to the bank.`);
    },
    onError: (err) => toast.error(err.message),
  });

  const accounts = useMemo(
    () => (accountsQ.data?.rows ?? []).filter((a) => a.type !== "credit_card"),
    [accountsQ.data],
  );

  // Default the pay-account picker to the run's account (if set) else the first.
  useEffect(() => {
    if (payAccountId) return;
    if (run?.payAccountId) setPayAccountId(run.payAccountId);
    else if (accounts[0]) setPayAccountId(accounts[0].id);
  }, [run?.payAccountId, accounts, payAccountId]);

  const breadcrumbs = [
    { label: "Payroll", to: "/payroll" as const },
    { label: "Runs", to: "/payroll" as const },
    { label: run ? periodLabel(run.periodMonth) : "Run" },
  ];

  if (runQ.isLoading) {
    return (
      <WorkspaceShell breadcrumbs={breadcrumbs}>
        <div className="rounded-md border border-line bg-paper p-6">
          <p className="text-sm text-muted">Loading run…</p>
        </div>
      </WorkspaceShell>
    );
  }
  if (runQ.error || !run) {
    return (
      <WorkspaceShell breadcrumbs={breadcrumbs}>
        <Alert intent="bad" title="Couldn't load run" description={runQ.error?.message ?? "Not found."} />
      </WorkspaceShell>
    );
  }

  const pill = statusPill(run.status);
  const aboveThreshold = BigInt(run.totalNetPaise) >= BigInt(run.approvalThresholdPaise);
  const period = periodLabel(run.periodMonth);

  return (
    <WorkspaceShell
      breadcrumbs={breadcrumbs}
      contentClassName="max-w-none h-full"
      viewportClassName="p-0"
      disableViewportScroll
    >
      <div className="flex h-full flex-col bg-bg">
        {/* Docked header */}
        <header className="shrink-0 border-b border-line bg-paper">
          <div className="flex items-center gap-3 px-6 py-3">
            <Link
              to="/payroll"
              aria-label="Back"
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-line text-ink hover:bg-bg"
            >
              <ArrowLeft size={18} strokeWidth={2.2} aria-hidden />
            </Link>
            <div className="min-w-0 flex-1">
              <h1 className="flex items-center gap-2 text-base font-bold text-ink">
                Payroll run — {period}
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${pill.className}`}
                >
                  {pill.label}
                </span>
              </h1>
              <p className="mt-0.5 text-xs text-muted sm:text-sm">
                {run.employeeCount} employees
                {run.payAccountName ? ` · paid from ${run.payAccountName}` : ""}
              </p>
            </div>
          </div>
        </header>

        {/* Scrollable body */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-bg">
          <div className="mx-auto w-full max-w-6xl space-y-4 px-6 py-5">
            {/* Tiles */}
            <PayrollKpiTiles
              tiles={[
                { icon: UsersRound, label: "Employees", value: String(run.employeeCount) },
                { icon: IndianRupee, label: "Gross", value: paiseToInr(run.totalGrossPaise) },
                { icon: MinusCircle, label: "Deductions", value: paiseToInr(run.totalDeductionsPaise) },
                { icon: Wallet, label: "Net payable", value: paiseToInr(run.totalNetPaise), net: true },
                {
                  icon: Landmark,
                  label: "Pay from",
                  value: run.payAccountName ?? "Not set",
                  text: true,
                },
              ]}
            />

            {/* Honesty seam */}
            <div className="flex items-start gap-3 rounded-md border border-dashed border-line-2 bg-raised p-3 text-xs text-muted">
              <span className="shrink-0 rounded border border-line-2 bg-paper px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide">
                V1.x
              </span>
              <p>
                <strong className="text-ink-2">Flint records, it doesn't calculate.</strong> You (or
                your CA) type each deduction — TDS, PF, ESI, PT. Flint only computes net pay and posts
                it to the bank. Statutory <strong>deposits</strong> (challan) and{" "}
                <strong>filings</strong> (Form 24Q, Form 16, PF ECR) are handled outside Flint in V1.
              </p>
            </div>

            {/* Pending-approval banner */}
            {run.pendingApprovalId ? (
              <Alert
                intent="info"
                title="Waiting for founder approval"
                description="This run's net pay is above the approval threshold. A founder approves it in the Inbox, then you can submit again to mark it approved."
              />
            ) : null}

            {/* Grid */}
            <div className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-line bg-raised text-right text-xs uppercase tracking-wide text-muted">
                    <th className="px-4 py-3 text-left font-semibold">Employee</th>
                    <th className="px-3 py-3 font-semibold">Gross</th>
                    {DEDUCTIONS.map((d) => (
                      <th key={d.key} className="px-3 py-3 font-semibold">
                        {d.label}
                      </th>
                    ))}
                    <th className="px-4 py-3 font-semibold">Net pay</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {run.lines.map((line) => (
                    <LineRow
                      key={line.id}
                      runId={runId}
                      line={line}
                      editable={isDraft}
                      onPersist={(key, paise) =>
                        updateLine.mutate({ runId, lineId: line.id, [key]: paise })
                      }
                      onPayslip={() => setSlipLine(line)}
                    />
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-line-2 bg-raised text-right font-mono font-semibold text-ink">
                    <td className="px-4 py-3 text-left font-sans text-xs uppercase tracking-wide text-muted">
                      Totals · {run.employeeCount} employees
                    </td>
                    <td className="px-3 py-3">{paiseToInr(run.totalGrossPaise)}</td>
                    <td className="px-3 py-3 text-muted" colSpan={DEDUCTIONS.length}>
                      − {paiseToInr(run.totalDeductionsPaise)}
                    </td>
                    <td className="px-4 py-3 text-sky-700">{paiseToInr(run.totalNetPaise)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Docked footer */}
        <footer className="shrink-0 border-t border-line bg-paper px-6 py-3 shadow-card">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-0 flex-1 text-sm text-muted">
              Net payable to {run.employeeCount} employees ·{" "}
              <strong className="font-mono text-ink">{paiseToInr(run.totalNetPaise)}</strong>
              {run.payAccountName ? ` from ${run.payAccountName}` : ""}
              {isDraft && aboveThreshold ? (
                <span className="ml-2 text-warn">· above approval threshold — needs founder sign-off</span>
              ) : null}
            </div>

            {isDraft ? (
              <>
                <span className="text-xs text-muted">{updateLine.isPending ? "Saving…" : "Draft saved"}</span>
                <button
                  type="button"
                  onClick={() =>
                    submit.mutate({
                      runId,
                      // After a founder approves the pending request in the Inbox,
                      // re-submitting must carry the approvalId so the domain can
                      // verify+consume it and advance the run to "approved".
                      // Without this, a re-submit just files a fresh approval and
                      // the run is stuck in awaiting_approval forever.
                      ...(run.pendingApprovalId ? { approvalId: run.pendingApprovalId } : {}),
                    })
                  }
                  disabled={submit.isPending || run.employeeCount === 0}
                  className="inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-2 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submit.isPending
                    ? "Submitting…"
                    : run.pendingApprovalId
                      ? "Mark approved"
                      : "Submit for approval"}
                </button>
              </>
            ) : null}

            {isApproved ? (
              <button
                type="button"
                onClick={() => setPayOpen(true)}
                disabled={pay.isPending}
                className="inline-flex h-10 items-center gap-2 rounded-sm bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Wallet size={15} aria-hidden />
                Pay net to bank
              </button>
            ) : null}

            {run.status === "paid" ? (
              <span className="inline-flex items-center rounded-full bg-ok-50 px-3 py-1.5 text-sm font-semibold text-ok">
                Paid
              </span>
            ) : null}
          </div>
        </footer>
      </div>

      {/* Payslip drawer */}
      <PayslipDrawer
        open={slipLine !== null}
        onOpenChange={(o) => !o && setSlipLine(null)}
        line={slipLine}
        companyName={companyQ.data?.legalName ?? "Your company"}
        periodLabel={period}
      />

      {/* Pay dialog — pick the account, confirm */}
      <DialogRoot open={payOpen} onOpenChange={setPayOpen}>
        <DialogPortal>
          <DialogOverlay />
          <DialogContent size="sm">
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-bold text-ink">Pay net to bank</h2>
                <p className="mt-0.5 text-sm text-muted">
                  Posts one salary entry per employee from the chosen account —{" "}
                  <strong className="font-mono text-ink">{paiseToInr(run.totalNetPaise)}</strong> across{" "}
                  {run.employeeCount} employees.
                </p>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-ink">Pay from</label>
                <Select value={payAccountId} onValueChange={setPayAccountId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose an account" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPayOpen(false)}
                  className="inline-flex h-10 items-center rounded-sm px-3 text-sm font-semibold text-ink hover:bg-bg"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => payAccountId && pay.mutate({ runId, payAccountId })}
                  disabled={pay.isPending || !payAccountId}
                  className="inline-flex h-10 items-center gap-2 rounded-sm bg-sky-600 px-4 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {pay.isPending ? "Paying…" : "Confirm payment"}
                </button>
              </div>
            </div>
          </DialogContent>
        </DialogPortal>
      </DialogRoot>
    </WorkspaceShell>
  );
}

// ── line row with live-recompute deduction inputs ──────────────────

function LineRow({
  line,
  editable,
  onPersist,
  onPayslip,
}: {
  runId: string;
  line: RunLineView;
  editable: boolean;
  onPersist: (key: DeductionKey, paise: string) => void;
  onPayslip: () => void;
}) {
  // Local draft of each deduction as a rupee string, seeded from the server.
  const [draft, setDraft] = useState<Record<DeductionKey, string>>(() => seed(line));

  // Re-seed when the server value changes (after a successful save / refetch).
  useEffect(() => {
    setDraft(seed(line));
  }, [line]);

  // Live net = gross − Σ (parsed drafts). Pure display; the server is truth.
  const liveNet = useMemo(() => {
    let net = BigInt(line.grossPaise);
    for (const d of DEDUCTIONS) {
      const paise = rupeesToPaiseString(draft[d.key]);
      net -= BigInt(paise ?? "0");
    }
    return net;
  }, [draft, line.grossPaise]);

  function commit(key: DeductionKey) {
    const paise = rupeesToPaiseString(draft[key]);
    if (paise === null) {
      // Bad input — reset to the server value.
      setDraft((d) => ({ ...d, [key]: paiseToRupeesInput(line[key]) }));
      return;
    }
    if (paise === line[key]) return; // no change
    onPersist(key, paise);
  }

  return (
    <tr className="border-b border-line text-right last:border-0 hover:bg-bg">
      <td className="px-4 py-2.5 text-left">
        <div className="flex items-center gap-3">
          <Avatar name={line.employeeName} photoUrl={line.photoUrl} />
          <div className="min-w-0">
            <div className="font-semibold text-ink">{line.employeeName}</div>
            <div className="text-xs text-muted">{line.designation ?? "—"}</div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2.5 font-mono font-semibold text-ink">{paiseToInr(line.grossPaise)}</td>
      {DEDUCTIONS.map((d) =>
        editable ? (
          <td key={d.key} className="px-2 py-2.5">
            <input
              inputMode="numeric"
              value={draft[d.key]}
              onChange={(e) => {
                // Capture .value BEFORE the setDraft updater runs — React 18
                // nullifies `event.currentTarget` after the handler returns,
                // so reading it inside the async updater closure throws.
                const value = e.currentTarget.value;
                setDraft((cur) => ({ ...cur, [d.key]: value }));
              }}
              onBlur={() => commit(d.key)}
              className="w-20 rounded-sm border border-line-2 bg-paper px-2 py-1 text-right font-mono text-sm text-ink focus:border-sky-600 focus:outline-none focus:ring-2 focus:ring-sky-100"
              aria-label={`${d.label} for ${line.employeeName}`}
            />
          </td>
        ) : (
          <td key={d.key} className="px-3 py-2.5 font-mono text-muted">
            {paiseToInr(line[d.key])}
          </td>
        ),
      )}
      <td className="px-4 py-2.5 font-mono font-semibold text-ink">{paiseToInr(liveNet)}</td>
      <td className="px-4 py-2.5 text-right">
        <button
          type="button"
          onClick={onPayslip}
          className="text-xs font-semibold text-sky-700 hover:underline"
        >
          Payslip
        </button>
      </td>
    </tr>
  );
}

function seed(line: RunLineView): Record<DeductionKey, string> {
  return {
    tdsPaise: paiseToRupeesInput(line.tdsPaise),
    pfPaise: paiseToRupeesInput(line.pfPaise),
    esiPaise: paiseToRupeesInput(line.esiPaise),
    ptPaise: paiseToRupeesInput(line.ptPaise),
    otherDeductionPaise: paiseToRupeesInput(line.otherDeductionPaise),
  };
}

function Avatar({ name, photoUrl }: { name: string; photoUrl: string | null }) {
  if (photoUrl) {
    return (
      <img
        src={photoUrl}
        alt=""
        aria-hidden
        className="h-8 w-8 shrink-0 rounded-lg object-cover shadow-card ring-1 ring-line"
      />
    );
  }
  const initials =
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase())
      .join("") || "?";
  return (
    <span
      aria-hidden
      className="inline-grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-sky-500 to-sky-700 text-xs font-bold text-white shadow-card"
    >
      {initials}
    </span>
  );
}
